import { useState, useCallback } from 'react';

/**
 * useGanttCursor Hook
 * 
 * Manages the DAW-style playhead cursor position in the timeline.
 * 
 * @param {React.RefObject} chartRef - Reference to the chart container
 * @param {React.RefObject} hasPannedRef - Reference tracking if user panned
 * @param {Function} clearSelection - Function to clear task selection
 * @returns {{ cursorPosition, handleTimelineClick }}
 */
export function useGanttCursor(chartRef, hasPannedRef, clearSelection) {
  // Cursor position (DAW-style playhead) - percentage position in timeline
  const [cursorPosition, setCursorPosition] = useState(null);

  // Handle timeline click to set cursor (DAW-style)
  // Only if we didn't pan (click vs drag distinction)
  const handleTimelineClick = useCallback((e) => {
    if (!chartRef.current) return;
    
    // Don't set cursor if we just panned (dragged more than 2px)
    if (hasPannedRef.current) return;
    
    // Don't set cursor if clicking on a bar (bar handles its own click)
    if (e.target.closest('.gantt-bar')) return;
    
    // Don't set cursor if clicking on sidebar
    if (e.target.closest('.gantt-row__sidebar')) return;
    if (e.target.closest('.gantt-timeline-header__sidebar')) return;
    
    // Clear selection when clicking empty space
    clearSelection();
    
    const timelineEl = e.target.closest('.gantt-row__timeline, .gantt-timeline-header__timeline');
    if (!timelineEl) return;
    
    const rect = timelineEl.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    
    setCursorPosition(Math.max(0, Math.min(100, percentage)));
  }, [chartRef, hasPannedRef, clearSelection]);

  return { cursorPosition, handleTimelineClick };
}
